import {
  keepPreviousData,
  useInfiniteQuery,
  useQueries,
  useQuery,
  type InfiniteData,
} from '@tanstack/react-query'

import { eventsApi } from '@/services/api/endpoints/eventsApi'
import type { EventsParams } from '@/services/api/params'
import { queryKeys } from '@/services/api/queryKeys'
import type { Event, EventType, EventsPage } from '@/services/api/schemas/events'

/** Размер страницы журнала: следующая подгружается, когда прокрутка доходит до конца списка. */
export const EVENTS_PAGE_SIZE = 30

export type EventsListParams = Pick<EventsParams, 'type' | 'from' | 'to'>

export interface EventsList {
  items: Event[]
  /** Сколько всего событий под фильтром (по последней загруженной странице). */
  total: number
}

/**
 * Страницы в плоский список. Пагинация по offset, а журнал живой: между запросами
 * страниц приходят новые события и сдвигают offset, поэтому элемент со стыка
 * страниц может повториться — дубли убираем по id.
 */
function flattenPages(data: InfiniteData<EventsPage>): EventsList {
  const seen = new Set<string>()
  const items: Event[] = []
  for (const page of data.pages) {
    for (const event of page.data) {
      if (seen.has(event.id)) continue
      seen.add(event.id)
      items.push(event)
    }
  }
  return { items, total: data.pages.at(-1)?.total ?? 0 }
}

export const useEventsQuery = (params: EventsListParams) =>
  useInfiniteQuery({
    queryKey: queryKeys.events({ ...params, limit: EVENTS_PAGE_SIZE }),
    queryFn: ({ pageParam }) =>
      eventsApi.getEvents({ ...params, limit: EVENTS_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const next = lastPage.offset + lastPage.data.length
      return lastPage.data.length > 0 && next < lastPage.total ? next : undefined
    },
    select: flattenPages,
    placeholderData: keepPreviousData,
    // Опрос перезапрашивает все загруженные страницы по очереди.
    refetchInterval: 30_000,
  })

/** Одна страница журнала без пагинации — для сводки последних событий на главной. */
export const useEventsPageQuery = (params: EventsParams) =>
  useQuery({
    queryKey: queryKeys.events(params),
    queryFn: () => eventsApi.getEvents(params),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  })

/** Ключ счётчика: тип события или «Все» (без фильтра типа — учитывает и неизвестные типы). */
export type CountKey = EventType | 'all'

/**
 * Счётчики для чипов фильтра. У API нет агрегата, поэтому на каждый ключ берём страницу
 * из одного элемента и читаем total; для «Все» — запрос без type, чтобы учесть события
 * типов, которых интерфейс не знает. Когда весь журнал уже загружен (total ≤ число
 * загруженных и фильтр «Все»), счётчики считаются из списка без запросов.
 */
export function useEventTypeCounts(
  from: string | undefined,
  keys: readonly CountKey[],
  enabled: boolean,
) {
  return useQueries({
    queries: keys.map((key) => {
      const params: EventsParams = { type: key === 'all' ? undefined : key, from, limit: 1 }
      return {
        queryKey: queryKeys.events(params),
        queryFn: () => eventsApi.getEvents(params),
        staleTime: 30_000,
        // Реже, чем список: счётчики не так критичны, а запросов на каждый ключ по одному.
        refetchInterval: 60_000,
        enabled,
      }
    }),
    combine: (results) => {
      const counts: Partial<Record<CountKey, number>> = {}
      keys.forEach((key, index) => {
        const total = results[index].data?.total
        if (total !== undefined) counts[key] = total
      })
      return counts
    },
  })
}
